import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fully white-labeled Nexus AI Assistant chat widget.
// Renders a native chat UI (no third-party iframe) and proxies messages through
// the NexusCMS backend, so the visitor's browser never talks to any external
// chatbot host. Renders nothing when the assistant is disabled for the tenant.
export const AiAssistantWidget = ({ tenantId }) => {
  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!tenantId) return;
    let active = true;
    fetch(`${API_URL}/api/public/ai-widget/${tenantId}`)
      .then((r) => r.json())
      .then((d) => { if (active && d?.enabled) setConfig(d); })
      .catch(() => {});
    return () => { active = false; };
  }, [tenantId]);

  // Seed the greeting the first time the panel opens.
  useEffect(() => {
    if (open && config && messages.length === 0) {
      setMessages([{ role: 'bot', text: config.greeting || 'Hi! How can I help you today?' }]);
    }
  }, [open, config]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  if (!config) return null;

  // Respect the tenant's placement choice (all pages vs. selected pages).
  if (config.widget_scope === 'selected') {
    const paths = config.widget_paths || [];
    const cur = (window.location.pathname || '/').replace(/\/+$/, '') || '/';
    const match = paths.some((p) => {
      const pp = (p || '/').replace(/\/+$/, '') || '/';
      return pp === cur || (pp !== '/' && cur.endsWith(pp));
    });
    if (!match) return null;
  }

  const accent = config.primary_color || '#4f46e5';

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text }]);
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/public/ai-widget/${tenantId}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, session_id: sessionRef.current }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'error');
      sessionRef.current = data.session_id || sessionRef.current;
      setMessages((m) => [...m, { role: 'bot', text: data.response, cta: data.cta || null, footer: data.footer || null }]);
    } catch (e) {
      setMessages((m) => [...m, { role: 'bot', text: "Sorry, I'm having trouble responding right now. Please try again." }]);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <div data-testid="ai-assistant-widget" className="fixed bottom-5 right-5 z-[9999] flex flex-col items-end">
      {open && (
        <div
          data-testid="ai-assistant-panel"
          className="mb-3 w-[min(380px,calc(100vw-2.5rem))] h-[min(560px,calc(100vh-7rem))] rounded-2xl overflow-hidden shadow-2xl border border-black/10 bg-white flex flex-col animate-in fade-in slide-in-from-bottom-4 duration-200"
        >
          {/* Header */}
          <div className="px-4 py-3 flex items-center gap-3 text-white shrink-0" style={{ backgroundColor: accent }}>
            <div className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center">
              <MessageCircle size={18} />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-sm truncate leading-tight">{config.bot_name || 'Assistant'}</p>
              <p className="text-[11px] text-white/80 leading-tight">Online</p>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close assistant"
              data-testid="ai-assistant-close"
              className="ml-auto h-7 w-7 rounded-full hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50" data-testid="ai-assistant-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex flex-col ${m.role === 'user' ? 'items-end' : 'items-start'}`}>
                <div
                  className={`max-w-[80%] px-3.5 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${
                    m.role === 'user' ? 'text-white rounded-br-sm' : 'bg-white border border-slate-200 text-slate-700 rounded-bl-sm'
                  }`}
                  style={m.role === 'user' ? { backgroundColor: accent } : undefined}
                >
                  {m.text}
                </div>
                {m.cta && m.cta.url && (
                  <a
                    href={m.cta.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="ai-assistant-cta"
                    className="mt-1.5 inline-block px-3.5 py-2 rounded-xl text-sm font-medium text-white shadow-sm hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: accent }}
                  >
                    {m.cta.label || 'Learn more'}
                  </a>
                )}
                {m.footer && (m.footer.message || m.footer.cta) && (
                  <div className="mt-1.5 max-w-[85%] flex flex-col items-start gap-1.5" data-testid="ai-assistant-footer">
                    {m.footer.message && (
                      <p className="text-xs text-slate-400 leading-snug">{m.footer.message}</p>
                    )}
                    {m.footer.cta && m.footer.cta.url && (
                      <a
                        href={m.footer.cta.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="ai-assistant-footer-cta"
                        className="inline-block px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors hover:bg-slate-50"
                        style={{ color: accent, borderColor: accent }}
                      >
                        {m.footer.cta.label || 'Learn more'}
                      </a>
                    )}
                  </div>
                )}
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 text-slate-400 px-3.5 py-2 rounded-2xl rounded-bl-sm text-sm flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> Typing…
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-3 border-t border-slate-200 bg-white shrink-0 flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              rows={1}
              placeholder="Type your message…"
              data-testid="ai-assistant-input"
              className="flex-1 resize-none max-h-24 text-sm px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-offset-0"
              style={{ '--tw-ring-color': accent }}
            />
            <button
              onClick={send}
              disabled={!input.trim() || sending}
              aria-label="Send message"
              data-testid="ai-assistant-send"
              className="h-9 w-9 shrink-0 rounded-full text-white flex items-center justify-center disabled:opacity-40 transition-opacity"
              style={{ backgroundColor: accent }}
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
      <button
        type="button"
        data-testid="ai-assistant-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close assistant' : 'Open assistant'}
        className="h-14 w-14 rounded-full text-white shadow-xl flex items-center justify-center transition-transform hover:scale-105 active:scale-95"
        style={{ backgroundColor: accent }}
      >
        {open ? <X size={24} /> : <MessageCircle size={26} />}
      </button>
    </div>
  );
};

export default AiAssistantWidget;
